import { ConditionOperatorEnumType, type ConditionOperator } from '@reach/shared-types/conditions';
import React, { useEffect } from 'react';
import { getOperatorsForFieldType } from './field-type-operators';
import type { BuilderFieldType } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  greater_than: 'greater than',
  less_than: 'less than',
  exists: 'exists',
  not_exists: 'does not exist',
  equals_month_day: 'On This Date (Any Year)',
  equals_month_day_year: 'On This Date (Specific Year)',
  between_month_day: 'Is Between Dates (Any Year)',
  between_month_day_year: 'Is Between Dates (Specific Year)',
  array_contains: 'array contains',
  contains: 'contains',
  not_contains: 'does not contain',
  in: 'is one of',
};

/**
 * Get the human-readable label for an operator, with special handling for date fields.
 * For date fields, "greater than" becomes "after" and "less than" becomes "before".
 */
const getOperatorLabel = (operator: ConditionOperator, fieldType: BuilderFieldType): string => {
  // Special case for date fields
  if (fieldType === 'date') {
    if (operator === 'greater_than') return 'after';
    if (operator === 'less_than') return 'before';
  }
  return OPERATOR_LABELS[operator] ?? operator;
};

interface OperatorSelectorProps {
  fieldType: BuilderFieldType;
  value: ConditionOperator;
  onChange: (value: ConditionOperator) => void;
  disabled?: boolean;
}

/**
 * Type-aware operator dropdown. Only shows operators valid for the selected field type.
 */
export const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  fieldType,
  value,
  onChange,
  disabled = false,
}) => {
  const options = getOperatorsForFieldType(fieldType);
  // For date fields, EQUALS_MONTH_DAY_YEAR is valid even though only EQUALS_MONTH_DAY is in the list.
  // We display it mapped to the base variant. BETWEEN_MONTH_DAY_YEAR is now directly in the list.
  const isEqualsYearVariant =
    fieldType === 'date' && value === ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR;
  const isValid = options.includes(value) || isEqualsYearVariant;
  const displayValue = isEqualsYearVariant
    ? ConditionOperatorEnumType.EQUALS_MONTH_DAY
    : isValid
      ? value
      : (options[0] ?? value);

  useEffect(() => {
    if (!isValid && options.length > 0 && options[0]) {
      onChange(options[0]);
    }
  }, [fieldType, isValid, options, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value as ConditionOperator);
  };

  return (
    <select
      className={styles.selectInput}
      value={displayValue}
      onChange={handleChange}
      disabled={disabled}
      aria-label="Condition operator">
      {options.map(op => (
        <option key={op} value={op}>
          {getOperatorLabel(op, fieldType)}
        </option>
      ))}
    </select>
  );
};
